import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreateRetentionPolicy } from "@/hooks/useArchive";
import { Shield, Plus, Save } from "lucide-react";

export function RetentionPolicyManager({ workspaceId }: { workspaceId: string }) {
  const createPolicy = useCreateRetentionPolicy();
  const [showForm, setShowForm] = useState(false);
  const [policy, setPolicy] = useState({
    policy_name: "",
    retention_period_years: 7,
    auto_archive_after_days: 90,
    legal_hold_enabled: false,
  });

  const handleSave = async () => {
    await createPolicy.mutateAsync({
      ...policy,
      workspace_id: workspaceId,
    });
    setShowForm(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Retention Policy Configuration
        </CardTitle>
        <CardDescription>
          Define how long bills are retained and when they're automatically archived
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm ? (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <div className="space-y-2">
              <Label>Policy Name</Label>
              <Input
                value={policy.policy_name}
                onChange={(e) => setPolicy({ ...policy, policy_name: e.target.value })}
                placeholder="e.g., Standard 7-Year Retention"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Retention Period (Years)</Label>
                <Select
                  value={policy.retention_period_years.toString()}
                  onValueChange={(value) => setPolicy({ ...policy, retention_period_years: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Years</SelectItem>
                    <SelectItem value="5">5 Years</SelectItem>
                    <SelectItem value="7">7 Years</SelectItem>
                    <SelectItem value="10">10 Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Auto-Archive After (Days)</Label>
                <Input
                  type="number"
                  value={policy.auto_archive_after_days}
                  onChange={(e) => setPolicy({ ...policy, auto_archive_after_days: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Legal Hold Capability</Label>
                <p className="text-xs text-muted-foreground">
                  Allow legal holds to prevent deletion
                </p>
              </div>
              <Switch
                checked={policy.legal_hold_enabled}
                onCheckedChange={(checked) => setPolicy({ ...policy, legal_hold_enabled: checked })}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={createPolicy.isPending}>
                <Save className="h-4 w-4 mr-2" />
                Save Policy
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Retention Policy
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
